import React, { useState, useRef, useEffect } from "react";
import authFetch from "../../authFetch";
import { TODAY } from "../../utils/format";
import { useValidationToast, validateFields } from "../../utils/validation";
import { transportCan, getAllUserProfiles, getEffectiveTaxonomy } from "../../permissions/checks";
import Modal from "../../shared/Modal";
import MiniRing from "../../shared/charts/MiniRing";
import { brandChartPalette } from "../../utils/color";
import useBnavFab from "../../shared/useBnavFab";
import { openExternalUrl } from "../../native";
import { enumLabel } from "../../i18n/enumLabels";
import { toIsoCountry } from "../../utils/geo";


/* ══════════════════════════════════════════════════════════
   PLACES AUTOCOMPLETE INPUT (Google Maps or Mapbox)

   Provider is resolved by the caller from platformApiKeys.mapsProvider
   ("google" | "mapbox") and the matching API key. The component's onChange
   contract and the directions URL it emits always target Google Maps, so
   drivers keep opening the same navigation app regardless of which
   provider was used to search.
══════════════════════════════════════════════════════════ */

/* ── Google Maps JS SDK loader (singleton) ── */
const _gmLoadState={loading:false,loaded:false,callbacks:[]};
function loadGoogleMaps(apiKey){
  if(!apiKey)return Promise.resolve(false);
  if(window.google?.maps?.places){_gmLoadState.loaded=true;return Promise.resolve(true);}
  if(_gmLoadState.loaded)return Promise.resolve(true);
  if(_gmLoadState.loading)return new Promise(r=>_gmLoadState.callbacks.push(r));
  _gmLoadState.loading=true;
  return new Promise(resolve=>{
    _gmLoadState.callbacks.push(resolve);
    const cbName="_gmReady"+Date.now();
    window[cbName]=()=>{
      _gmLoadState.loaded=true;_gmLoadState.loading=false;
      _gmLoadState.callbacks.forEach(cb=>cb(!!window.google?.maps?.places));
      _gmLoadState.callbacks=[];
      delete window[cbName];
    };
    const s=document.createElement("script");
    s.src=`https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${cbName}`;
    s.async=true;s.defer=true;
    s.onerror=()=>{
      _gmLoadState.loading=false;
      _gmLoadState.callbacks.forEach(cb=>cb(false));
      _gmLoadState.callbacks=[];
      delete window[cbName];
    };
    document.head.appendChild(s);
  });
}

/* Google Maps → always the destination for the `mapsUrl` we hand back,
   independent of the autocomplete provider. */
const buildGoogleMapsUrl=(lat,lng,fallbackText)=>
  lat&&lng
    ?`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    :`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fallbackText||"")}`;

function PlacesAutocomplete({value,onChange,placeholder,apiKey,provider,isRTL,className,country,proximity}){
  const activeProvider=provider==="mapbox"?"mapbox":"google";
  const [query,setQuery]=useState(value||"");
  const [suggestions,setSuggestions]=useState([]);
  const [showDrop,setShowDrop]=useState(false);
  const debounceRef=useRef(null);
  const wrapRef=useRef(null);
  const serviceRef=useRef(null);
  const geocoderRef=useRef(null);
  const readyRef=useRef(activeProvider==="mapbox"?!!apiKey:!!window.google?.maps?.places);
  const [,forceUpdate]=useState(0);
  const internalChange=useRef(false);

  useEffect(()=>{
    if(internalChange.current){internalChange.current=false;return;}
    setQuery(value||"");
  },[value]);

  /* Mapbox is a plain REST call — readiness is just "we have a key". */
  useEffect(()=>{
    if(activeProvider==="mapbox"){
      readyRef.current=!!apiKey;
      forceUpdate(n=>n+1);
      return;
    }
    if(!apiKey)return;
    if(readyRef.current){
      if(!serviceRef.current)serviceRef.current=new window.google.maps.places.AutocompleteService();
      if(!geocoderRef.current)geocoderRef.current=new window.google.maps.Geocoder();
      return;
    }
    loadGoogleMaps(apiKey).then(ok=>{
      if(ok&&window.google?.maps?.places){
        readyRef.current=true;
        serviceRef.current=new window.google.maps.places.AutocompleteService();
        geocoderRef.current=new window.google.maps.Geocoder();
        forceUpdate(n=>n+1);
      }
    });
  },[apiKey,activeProvider]);

  useEffect(()=>{
    const h=e=>{if(wrapRef.current&&!wrapRef.current.contains(e.target))setShowDrop(false);};
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[]);

  /* Mapbox Geocoding v6 forward returns both suggestions and coords in
     one call, so we store the coords on each suggestion and skip a
     second round-trip on selection. */
  const searchMapbox=async(text)=>{
    if(!apiKey)return;
    try{
      const params=new URLSearchParams({
        q:text,
        autocomplete:"true",
        limit:"6",
        language:isRTL?"ar":"en",
        access_token:apiKey,
      });
      /* Hard country filter — single or comma-separated ISO codes.
         Dramatically improves result relevance for KSA users who'd
         otherwise get US/EG/IL hits for common place names. */
      if(country)params.set("country",String(country).toLowerCase());
      /* Soft location bias — Mapbox ranks results near this coord higher. */
      if(proximity&&proximity.lat!=null&&proximity.lng!=null){
        params.set("proximity",`${proximity.lng},${proximity.lat}`);
      }
      const url=`https://api.mapbox.com/search/geocode/v6/forward?${params.toString()}`;
      const resp=await fetch(url);
      if(!resp.ok){setSuggestions([]);return;}
      const data=await resp.json();
      const feats=Array.isArray(data?.features)?data.features:[];
      setSuggestions(feats.map(f=>{
        const [lng,lat]=f.geometry?.coordinates||[null,null];
        const full=f.properties?.full_address||f.properties?.place_formatted||f.properties?.name||"";
        return{
          placeId:f.properties?.mapbox_id||f.id||"",
          description:full,
          mainText:f.properties?.name||full,
          lat,lng,
        };
      }));
      setShowDrop(true);
    }catch{setSuggestions([]);}
  };

  const searchGoogle=(text)=>{
    if(!readyRef.current||!serviceRef.current)return;
    const req={input:text,language:isRTL?"ar":"en"};
    if(country)req.componentRestrictions={country:String(country).toLowerCase()};
    if(proximity&&proximity.lat!=null&&proximity.lng!=null&&window.google?.maps?.LatLng){
      req.locationBias={center:new window.google.maps.LatLng(proximity.lat,proximity.lng),radius:50000};
    }
    serviceRef.current.getPlacePredictions(req,(predictions,status)=>{
      if(status===window.google.maps.places.PlacesServiceStatus.OK&&predictions){
        setSuggestions(predictions.map(p=>({placeId:p.place_id,description:p.description,mainText:p.structured_formatting?.main_text||p.description,lat:null,lng:null})));
        setShowDrop(true);
      }else{
        setSuggestions([]);
      }
    });
  };

  const search=(text)=>{
    if(!text||text.length<3){setSuggestions([]);return;}
    clearTimeout(debounceRef.current);
    debounceRef.current=setTimeout(()=>{
      if(activeProvider==="mapbox")searchMapbox(text);
      else searchGoogle(text);
    },350);
  };

  const pick=(s)=>{
    internalChange.current=true;
    setQuery(s.description);
    setShowDrop(false);
    setSuggestions([]);
    if(activeProvider==="mapbox"){
      onChange({address:s.description,placeId:s.placeId||"",lat:s.lat??null,lng:s.lng??null,mapsUrl:buildGoogleMapsUrl(s.lat,s.lng,s.description)});
      return;
    }
    if(geocoderRef.current&&s.placeId){
      geocoderRef.current.geocode({placeId:s.placeId},(results,status)=>{
        let lat=null,lng=null;
        if(status==="OK"&&results[0]?.geometry?.location){
          lat=results[0].geometry.location.lat();
          lng=results[0].geometry.location.lng();
        }
        onChange({address:s.description,placeId:s.placeId||"",lat,lng,mapsUrl:buildGoogleMapsUrl(lat,lng,s.description)});
      });
    }else{
      onChange({address:s.description,placeId:s.placeId||"",lat:null,lng:null,mapsUrl:buildGoogleMapsUrl(null,null,s.description)});
    }
  };

  const handleInput=(text)=>{
    internalChange.current=true;
    setQuery(text);
    setShowDrop(true);
    search(text);
    onChange({address:text,placeId:"",lat:null,lng:null,mapsUrl:text?buildGoogleMapsUrl(null,null,text):""});
  };

  const providerLabel=activeProvider==="mapbox"?"Mapbox":"Google Maps";
  return(
    <div ref={wrapRef} style={{position:"relative"}}>
      <input className={className||"fi"} value={query} onChange={e=>handleInput(e.target.value)} onFocus={()=>{if(suggestions.length)setShowDrop(true);}} placeholder={placeholder}/>
      {apiKey&&activeProvider==="google"&&!readyRef.current&&<div style={{fontSize:9,color:"var(--g400)",marginTop:2}}>{isRTL?"جارٍ تحميل الخرائط…":"Loading Maps SDK..."}</div>}
      {!apiKey&&<div style={{fontSize:9,color:"var(--red,#dc2626)",marginTop:2}}>{isRTL?`لم يتم تكوين مفتاح ${providerLabel}`:`No ${providerLabel} API key configured`}</div>}
      {showDrop&&suggestions.length>0&&(
        <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:999,background:"var(--white)",border:"1px solid var(--g200)",borderRadius:"0 0 var(--r) var(--r)",boxShadow:"var(--sh1)",maxHeight:200,overflowY:"auto"}}>
          {suggestions.map((s,i)=>(
            <div key={s.placeId||i} onClick={()=>pick(s)} style={{padding:"8px 12px",fontSize:13,cursor:"pointer",borderBottom:i<suggestions.length-1?"1px solid var(--g100)":"none"}}
              onMouseEnter={e=>e.currentTarget.style.background="var(--g50)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{fontWeight:500}}>{s.mainText}</div>
              {s.description!==s.mainText&&<div style={{fontSize:11,color:"var(--g400)",marginTop:1}}>{s.description}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   TRANSPORTATION SCHEDULE MODULE
══════════════════════════════════════════════════════════ */
function TransportModule({state,dispatch,t,platformCtx,currentUser,driverLock}){
  const {transportRequests,users,locations,vehicles,lang}=state;
  /* driverLock: when set (e.g. the staff workspace), the list is
     pre-filtered to this driver's rides, mutation affordances (FAB,
     Book modal, status cancel/complete) are hidden, and cross-user
     analytics are suppressed. Mirrors TaskEnginePanel's assigneeLock. */
  const tCfg=state.transportConfig||{};
  /* Phase 2.4: resolve Transport taxonomy once — used by both the
     initial create path and the retry path. Looks up by legacy value
     so the mapping stays obvious; falls back to the first entry. */
  const _tTax=getEffectiveTaxonomy(state,platformCtx,"Transport");
  const resolveTransportTaxId=(recurring)=>{
    const want=recurring?"recurring_ride":"ride";
    const e=_tTax.find(x=>x.value===want)||_tTax[0]||null;
    return e?e.id:null;
  };
  const platformKeys=platformCtx?.platformApiKeys||{};
  const wsKeys=state.apiKeys||{};
  const mapsProvider=platformKeys.mapsProvider||wsKeys.mapsProvider||"google";
  const mapsApiKey=mapsProvider==="mapbox"
    ?(platformKeys.mapbox||wsKeys.mapbox||"")
    :(platformKeys.googleMaps||wsKeys.googleMaps||"");
  /* Default pickup left blank — user selects via autocomplete */
  const activeLocObj=state.locFilter?locations.find(l=>l.id===state.locFilter):locations.find(l=>l.isPrimary)||locations[0];
  /* Bias autocomplete: ISO country from workspace setup, proximity from
     the active location so searches lean toward the user's city, not
     world-wide. Both providers accept these as soft/hard hints. */
  const mapsCountry=toIsoCountry(state.householdData?.country||"Saudi Arabia");
  const mapsProximity=(activeLocObj?.lat!=null&&activeLocObj?.lng!=null)
    ?{lat:activeLocObj.lat,lng:activeLocObj.lng}
    :null;
  const defaultPickup="";
  const defaultPickupMeta={placeId:"",lat:null,lng:null,mapsUrl:""};

  const allVehicles=(vehicles||[]).filter(v=>v.status!=="sold"&&v.status!=="scrapped");
  /* Resolve vehicle assigned to a driver */
  const vehicleForDriver=(driverId)=>driverId?allVehicles.find(v=>v.driverId===driverId):null;
  const vehicleLabel=(v)=>v?`${v.make||""} ${v.model||""} ${v.plate?`(${v.plate})`:""}`.trim():"";
  const vehicleInitials=(v)=>{if(!v)return"--";const m=v.make||"";return m.slice(0,2).toUpperCase()||"--";};

  const [showAdd,setShowAdd]=useState(false);useBnavFab(driverLock?()=>{}:()=>setShowAdd(true));
  const [form,setForm]=useState({userId:currentUser?.id||"",pickupLoc:defaultPickup,pickupPlaceId:defaultPickupMeta.placeId,pickupLat:defaultPickupMeta.lat,pickupLng:defaultPickupMeta.lng,pickupMapsUrl:defaultPickupMeta.mapsUrl,dropoffLoc:"",dropoffPlaceId:"",dropoffLat:null,dropoffLng:null,dropoffMapsUrl:"",date:TODAY,time:"09:00",driverId:"",vehicleId:"",notes:"",locId:activeLocObj?.id||"",recurring:false,frequency:"weekly",repeatUntil:""});
  const [bookErr,setBookErr]=useState("");
  const [failedTaskIds,setFailedTaskIds]=useState([]); /* transport request IDs whose task creation failed */
  const [filterStatus,_setFilterStatus]=useState(tCfg.savedFilterStatus||"all");
  const setFilterStatus=(v)=>{_setFilterStatus(v);dispatch({type:"SET_TRANSPORT_CONFIG",patch:{savedFilterStatus:v}});};
  const [editId,setEditId]=useState(null);
  const [editForm,setEditForm]=useState({});
  const [detailId,setDetailId]=useState(null); /* ride detail bottom sheet */
  const isRTL=lang==="ar";
  const vt=useValidationToast();
  const API=typeof window!=="undefined"?window.location.origin:"";
  const allProfiles=getAllUserProfiles(state,platformCtx);
  const staffProfileIds=new Set(allProfiles.filter(p=>p.group==="Staff").map(p=>p.id));

  /* Eligible drivers: Staff-group profiles whose id indicates a driving
     role (id contains "driver" — so staff_driver, driver, shuttle_driver,
     night_driver, or any workspace-defined driving role all qualify).
     This is a real-world domain constraint: a gardener or cook is NOT
     going to drive the family. If a workspace needs another role to
     drive, the owner names it with "driver" in the id, or we add a
     dedicated canDrive capability later. */
  const driverProfileIds=new Set(allProfiles.filter(p=>p.group==="Staff"&&p.id.toLowerCase().includes("driver")).map(p=>p.id));
  const drivers=users.filter(u=>u.active&&(driverProfileIds.has(u.role)||u.role==="driver"));
  /* Family / rider list: active users whose profile is NOT in the Staff
     group. Legacy "driver" / "maid" literal strings still excluded for
     pre-migration workspaces. */
  const familyUsers=users.filter(u=>u.active&&!staffProfileIds.has(u.role)&&u.role!=="driver"&&u.role!=="maid");

  /* Permission checks — driverLock hard-offs all mutations so the staff
     workspace never shows a create / edit / cancel affordance. */
  const canBook  =!driverLock&&transportCan("bookTransport",  currentUser,state,platformCtx);
  const canEdit  =!driverLock&&transportCan("editTransport",  currentUser,state,platformCtx);
  const canManage=!driverLock&&transportCan("manageTransport",currentUser,state,platformCtx);

  /* Profile-aware display name */
  const roleName=(roleId)=>{const p=allProfiles.find(pp=>pp.id===roleId);return p?(isRTL&&p.nameAr?p.nameAr:p.name):roleId;};

  const generateDates=(startDate,frequency,until)=>{
    const dates=[];
    const start=new Date(startDate);
    const end=until?new Date(until):new Date(start.getTime()+90*24*60*60*1000);
    let cur=new Date(start);
    while(cur<=end&&dates.length<52){
      dates.push(cur.toISOString().slice(0,10));
      const next=new Date(cur);
      if(frequency==="daily")next.setDate(next.getDate()+1);
      else if(frequency==="weekly")next.setDate(next.getDate()+7);
      else if(frequency==="biweekly")next.setDate(next.getDate()+14);
      else if(frequency==="monthly")next.setMonth(next.getMonth()+1);
      else if(frequency==="weekdays"){
        next.setDate(next.getDate()+1);
        while(next.getDay()===5||next.getDay()===6)next.setDate(next.getDate()+1); /* Fri+Sat weekend (Saudi) */
      }
      cur=next;
    }
    return dates;
  };

  /* Resolve driver: explicit pick, config default, or first available */
  const resolveDriver=(driverId)=>{
    if(driverId)return drivers.find(d=>d.id===driverId);
    if(tCfg.defaultAssigneeRole){
      const byRole=drivers.find(d=>d.role===tCfg.defaultAssigneeRole);
      if(byRole)return byRole;
    }
    return drivers[0]||null;
  };

  /* Driver conflict check */
  const conflictBuffer=tCfg.conflictBufferMinutes||platformCtx?.platformTransportConfig?.conflictBufferMinutes||45;
  const [conflicts,setConflicts]=useState([]);

  const checkConflicts=(driverId,dates,time)=>{
    if(!driverId||!time)return [];
    const rideMinutes=parseInt(time.split(":")[0])*60+parseInt(time.split(":")[1]);
    const found=[];
    for(const date of dates){
      const existing=(transportRequests||[]).filter(r=>r.driverId===driverId&&r.date===date&&r.status==="scheduled");
      for(const r of existing){
        if(!r.time)continue;
        const existMin=parseInt(r.time.split(":")[0])*60+parseInt(r.time.split(":")[1]);
        if(Math.abs(rideMinutes-existMin)<conflictBuffer){
          found.push({date,time:r.time,pickupLoc:r.pickupLoc,dropoffLoc:r.dropoffLoc,id:r.id});
        }
      }
    }
    return found;
  };

  /* Build Google Maps directions URL for driver */
  const buildDirectionsUrl=(pickup,dropoff)=>{
    const origin=pickup.lat&&pickup.lng?`${pickup.lat},${pickup.lng}`:pickup.address;
    const dest=dropoff.lat&&dropoff.lng?`${dropoff.lat},${dropoff.lng}`:dropoff.address;
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}`;
  };

  /* Re-check conflicts only while the booking modal is open */
  useEffect(()=>{
    if(!showAdd){setConflicts([]);return;}
    const driver=resolveDriver(form.driverId);
    if(!driver||!form.time){setConflicts([]);return;}
    const dates=form.recurring?generateDates(form.date,form.frequency,form.repeatUntil):[form.date];
    setConflicts(checkConflicts(driver.id,dates,form.time));
  },[showAdd,form.driverId,form.date,form.time,form.recurring,form.frequency,form.repeatUntil,transportRequests,drivers,conflictBuffer]); // eslint-disable-line react-hooks/exhaustive-deps

  const book=async()=>{
    /* Validate required fields */
    const m=validateFields([{ok:form.pickupLoc,label:isRTL?"موقع الإقلاع":"Pickup Location"},{ok:form.dropoffLoc,label:isRTL?"موقع الوصول":"Dropoff Location"},{ok:form.time,label:isRTL?"الوقت":"Time"},{ok:form.date,label:isRTL?"التاريخ":"Date"}]);
    if(m.length){vt.show(m.join(", "));setBookErr(m.join(". "));return;}
    setBookErr("");

    const driver=resolveDriver(form.driverId);
    const groupId=`tg${Date.now()}`;
    const dates=form.recurring?generateDates(form.date,form.frequency,form.repeatUntil):[form.date];
    const directionsUrl=buildDirectionsUrl(
      {address:form.pickupLoc,lat:form.pickupLat,lng:form.pickupLng},
      {address:form.dropoffLoc,lat:form.dropoffLat,lng:form.dropoffLng}
    );

    /* Dispatch all transport requests immediately so rides appear straight away */
    const reqIds=[];
    for(let i=0;i<dates.length;i++){
      const date=dates[i];
      const reqId=`tr${Date.now()}_${i}`;
      reqIds.push({reqId,date});
      const resolvedVehicleId=form.vehicleId||(driver?vehicleForDriver(driver.id)?.id:"")||"";
      dispatch({type:"ADD_TRANSPORT",req:{
        ...form,id:reqId,date,status:"scheduled",taskId:"",
        driverId:driver?.id||"",vehicleId:resolvedVehicleId,locId:form.locId||"",
        groupId:form.recurring?groupId:null,
        isRecurring:form.recurring,
        frequencyLabel:form.recurring?form.frequency:null,
        pickupPlaceId:form.pickupPlaceId,pickupLat:form.pickupLat,pickupLng:form.pickupLng,pickupMapsUrl:form.pickupMapsUrl,
        dropoffPlaceId:form.dropoffPlaceId,dropoffLat:form.dropoffLat,dropoffLng:form.dropoffLng,dropoffMapsUrl:form.dropoffMapsUrl,
        directionsUrl,
      }});
    }

    /* Notify driver (once, summarising) */
    if(driver){
      const label=form.recurring?`${dates.length} trips · starts ${form.date}`:`${form.date} ${form.time}`;
      dispatch({type:"ADD_NOTIF",notif:{id:`n${Date.now()}`,userId:driver.id,type:"transport",
        title:isRTL?"مهمة تنقل جديدة":"New transport task",
        body:`${form.time} · ${form.pickupLoc} → ${form.dropoffLoc} · ${label}`,
        link:"Transport",read:false,time:new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}),date:TODAY}});
    }
    dispatch({type:"ADD_NOTIF",notif:{id:`n${Date.now()+1}`,userId:form.userId,type:"transport",
      title:isRTL?"تأكيد الحجز":"Booking confirmed",
      body:`${form.pickupLoc} → ${form.dropoffLoc} · ${form.recurring?`Repeats ${form.frequency}`:form.date}`,
      link:"Transport",read:false,time:new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}),date:TODAY}});

    /* Close modal immediately so rides appear in the list */
    const savedForm={...form};
    setShowAdd(false);
    setForm({userId:currentUser?.id||"",pickupLoc:defaultPickup,pickupPlaceId:defaultPickupMeta.placeId,pickupLat:defaultPickupMeta.lat,pickupLng:defaultPickupMeta.lng,pickupMapsUrl:defaultPickupMeta.mapsUrl,dropoffLoc:"",dropoffPlaceId:"",dropoffLat:null,dropoffLng:null,dropoffMapsUrl:"",date:TODAY,time:"09:00",driverId:"",vehicleId:"",notes:"",locId:activeLocObj?.id||"",recurring:false,frequency:"weekly",repeatUntil:""});

    /* Create linked tasks in background */
    const slaConfig={};
    if(tCfg.slaDays)slaConfig.expected_completion_days=parseFloat(tCfg.slaDays);
    const failed=[];
    for(const {reqId,date} of reqIds){
      if(tCfg.autoCreateTask!==false){
        try{
          const res=await authFetch(`${API}/api/tasks?action=create_task`,{method:"POST",headers:{"Content-Type":"application/json"},
            body:JSON.stringify({ws_key:state.wsKey,actor_id:currentUser?.id,actor_name:currentUser?.name,
              task:{title:`Transport — ${savedForm.pickupLoc} → ${savedForm.dropoffLoc}`,
                title_ar:`نقل — ${savedForm.pickupLoc} → ${savedForm.dropoffLoc}`,
                description:`${date} ${savedForm.time} · ${savedForm.pickupLoc} → ${savedForm.dropoffLoc}`,
                priority:tCfg.defaultPriority||"medium",due_date:date||null,scheduled_time:date&&savedForm.time?`${date}T${savedForm.time}`:null,
                created_by:currentUser?.id||null,
                assigned_to:driver?.id||null,assigned_to_name:driver?.name||"",
                source_module:"Transport",task_type:savedForm.recurring?"recurring_ride":"ride",
                task_type_id:resolveTransportTaxId(savedForm.recurring),
                loc_id:savedForm.locId||null,
                context_payload:{transportIds:[reqId],pickupLoc:savedForm.pickupLoc,dropoffLoc:savedForm.dropoffLoc,
                  date,time:savedForm.time,riderId:savedForm.userId,groupId:savedForm.recurring?groupId:null,
                  pickupMapsUrl:savedForm.pickupMapsUrl||"",dropoffMapsUrl:savedForm.dropoffMapsUrl||"",
                  directionsUrl,taxonomyId:resolveTransportTaxId(savedForm.recurring),
                  pickupPlaceId:savedForm.pickupPlaceId||"",dropoffPlaceId:savedForm.dropoffPlaceId||""},
                linked_entities:[{type:"transport_request",id:reqId}],
                requires_approval:tCfg.requireApproval||false,
                requires_expense:tCfg.requireExpense||false,
                approval_roles:tCfg.approvalRoles||["owner"],
                sla_config:Object.keys(slaConfig).length?slaConfig:undefined}})});
          const data=await res.json();
          if(data.ok&&data.task){
            dispatch({type:"PATCH_TRANSPORT_LOCAL",id:reqId,patch:{taskId:data.task.id}});
            dispatch({type:"ADD_TASK",task:data.task});
          }else{failed.push(reqId);}
        }catch(e){console.error("[Transport] task creation failed",e);failed.push(reqId);}
      }
    }
    if(failed.length>0){
      setFailedTaskIds(prev=>[...prev,...failed]);
      vt.show(isRTL?`فشل إنشاء ${failed.length} مهمة — يمكنك إعادة المحاولة`:`${failed.length} task${failed.length>1?"s":""} failed to create — you can retry`);
    }
  };

  /* Retry task creation for rides that failed */
  const retryTaskCreation=async(reqId)=>{
    const r=(transportRequests||[]).find(x=>x.id===reqId);
    if(!r||r.taskId)return;
    const driver=users.find(u=>u.id===r.driverId);
    const slaConfig={};
    if(tCfg.slaDays)slaConfig.expected_completion_days=parseFloat(tCfg.slaDays);
    try{
      const res=await authFetch(`${API}/api/tasks?action=create_task`,{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ws_key:state.wsKey,actor_id:currentUser?.id,actor_name:currentUser?.name,
          task:{title:`Transport — ${r.pickupLoc} → ${r.dropoffLoc}`,
            title_ar:`نقل — ${r.pickupLoc} → ${r.dropoffLoc}`,
            description:`${r.date} ${r.time} · ${r.pickupLoc} → ${r.dropoffLoc}`,
            priority:tCfg.defaultPriority||"medium",due_date:r.date||null,scheduled_time:r.date&&r.time?`${r.date}T${r.time}`:null,
            created_by:currentUser?.id||null,
            assigned_to:driver?.id||null,assigned_to_name:driver?.name||"",
            source_module:"Transport",task_type:r.isRecurring?"recurring_ride":"ride",
            task_type_id:resolveTransportTaxId(r.isRecurring),
            loc_id:r.locId||null,
            context_payload:{transportIds:[reqId],pickupLoc:r.pickupLoc,dropoffLoc:r.dropoffLoc,
              date:r.date,time:r.time,riderId:r.userId,groupId:r.groupId||null,
              pickupMapsUrl:r.pickupMapsUrl||"",dropoffMapsUrl:r.dropoffMapsUrl||"",
              directionsUrl:r.directionsUrl||"",taxonomyId:resolveTransportTaxId(r.isRecurring),
              pickupPlaceId:r.pickupPlaceId||"",dropoffPlaceId:r.dropoffPlaceId||""},
            linked_entities:[{type:"transport_request",id:reqId}],
            requires_approval:tCfg.requireApproval||false,
            requires_expense:tCfg.requireExpense||false,
            approval_roles:tCfg.approvalRoles||["owner"],
            sla_config:Object.keys(slaConfig).length?slaConfig:undefined}})});
      const data=await res.json();
      if(data.ok&&data.task){
        dispatch({type:"PATCH_TRANSPORT_LOCAL",id:reqId,patch:{taskId:data.task.id}});
        dispatch({type:"ADD_TASK",task:data.task});
        setFailedTaskIds(prev=>prev.filter(id=>id!==reqId));
      }else{vt.show(isRTL?"فشلت إعادة المحاولة":"Retry failed");}
    }catch(e){console.error("[Transport] task retry failed",e);vt.show(isRTL?"فشلت إعادة المحاولة":"Retry failed");}
  };

  /* Edit handler */
  const [editApplyAll,setEditApplyAll]=useState(false);
  const saveEdit=()=>{
    const req=(transportRequests||[]).find(r=>r.id===editId);
    if(editApplyAll&&req?.groupId){
      /* Apply edits to all future scheduled rides in this recurring series */
      const futureRides=(transportRequests||[]).filter(r=>r.groupId===req.groupId&&r.status==="scheduled"&&r.date>=TODAY);
      const {date,...bulkPatch}=editForm; /* exclude date from bulk — each keeps its own date */
      for(const fr of futureRides){
        dispatch({type:"UPDATE_TRANSPORT",id:fr.id,patch:bulkPatch});
        if(fr.taskId&&bulkPatch.driverId&&bulkPatch.driverId!==fr.driverId){
          dispatch({type:"UPDATE_TASK",id:fr.taskId,patch:{assignedTo:bulkPatch.driverId,assignedToName:users.find(u=>u.id===bulkPatch.driverId)?.name||""}});
        }
      }
    }else{
      dispatch({type:"UPDATE_TRANSPORT",id:editId,patch:editForm});
      if(req?.taskId&&editForm.driverId&&editForm.driverId!==req.driverId){
        dispatch({type:"UPDATE_TASK",id:req.taskId,patch:{assignedTo:editForm.driverId,assignedToName:users.find(u=>u.id===editForm.driverId)?.name||""}});
      }
    }
    setEditId(null);setEditApplyAll(false);
  };

  /* Cancel all future rides in a recurring series */
  const cancelFutureSeries=(groupId)=>{
    const futureRides=(transportRequests||[]).filter(r=>r.groupId===groupId&&r.status==="scheduled"&&r.date>=TODAY);
    for(const r of futureRides){updateRideStatus(r.id,"cancelled");}
  };

  /* Update ride status + notify rider and driver */
  const updateRideStatus=(rideId,newStatus)=>{
    dispatch({type:"UPDATE_TRANSPORT",id:rideId,patch:{status:newStatus}});
    const r=(transportRequests||[]).find(x=>x.id===rideId);
    if(!r)return;
    const driver=users.find(u=>u.id===r.driverId);
    const statusLabel=newStatus==="completed"?(isRTL?"مكتملة":"Completed"):(isRTL?"ملغاة":"Cancelled");
    const title=newStatus==="completed"?(isRTL?"رحلة مكتملة":"Ride Completed"):(isRTL?"رحلة ملغاة":"Ride Cancelled");
    const body=`${r.pickupLoc} → ${r.dropoffLoc} · ${r.date} ${r.time} — ${statusLabel}`;
    const now=new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
    /* Notify rider */
    if(r.userId){dispatch({type:"ADD_NOTIF",notif:{id:`n${Date.now()}`,userId:r.userId,type:"transport",title,body,link:"Transport",read:false,time:now,date:TODAY}});}
    /* Notify driver */
    if(r.driverId&&r.driverId!==r.userId){dispatch({type:"ADD_NOTIF",notif:{id:`n${Date.now()+1}`,userId:r.driverId,type:"transport",title,body,link:"Transport",read:false,time:now,date:TODAY}});}
  };

  const [viewMode,_setViewMode]=useState(tCfg.savedViewMode||"week"); /* "list" | "week" */
  const setViewMode=(v)=>{_setViewMode(v);dispatch({type:"SET_TRANSPORT_CONFIG",patch:{savedViewMode:v}});};
  const [weekOffset,setWeekOffset]=useState(0);
  const [trnMobilePage,setTrnMobilePage]=useState(0);
  const [trnDateFilter,_setTrnDateFilter]=useState(tCfg.savedDateFilter||"upcoming"); /* "all" | "today" | "week" | "upcoming" */
  const setTrnDateFilter=(v)=>{_setTrnDateFilter(v);dispatch({type:"SET_TRANSPORT_CONFIG",patch:{savedDateFilter:v}});};

  /* Week calendar helpers — Saudi week starts Sunday */
  const getWeekStart=(offset)=>{
    const d=new Date();d.setDate(d.getDate()+offset*7);
    const day=d.getDay();
    d.setDate(d.getDate()-day); /* rewind to Sunday */
    return d;
  };
  const weekStart=getWeekStart(weekOffset);
  const weekDays=Array.from({length:7},(_,i)=>{const d=new Date(weekStart);d.setDate(d.getDate()+i);return d.toISOString().slice(0,10);});
  const dayLabelsEn=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const dayLabelsAr=["أحد","إثنين","ثلاثاء","أربعاء","خميس","جمعة","سبت"];
  const weekLabel=`${weekDays[0]} — ${weekDays[6]}`;

  const filtered=(transportRequests||[]).filter(r=>filterStatus==="all"||r.status===filterStatus).sort((a,b)=>a.date.localeCompare(b.date)||a.time.localeCompare(b.time));
  const byDate={};
  filtered.forEach(r=>{if(!byDate[r.date])byDate[r.date]=[];byDate[r.date].push(r);});
  const sortedDates=Object.keys(byDate).sort((a,b)=>a.localeCompare(b));

  /* Ride card — shared between list and week views (clickable → opens detail sheet) */
  const renderRideCard=(r,compact)=>{
    const driver=users.find(u=>u.id===r.driverId);
    const rider=users.find(u=>u.id===r.userId);
    const vehicle=r.vehicleId?allVehicles.find(v=>v.id===r.vehicleId):null;
    const initials=vehicle?vehicleInitials(vehicle):(r.isRecurring?"↻":"--");
    return(
      <div key={r.id} style={{background:"var(--white)",border:"1px solid var(--g200)",borderRadius:"var(--r2)",padding:compact?"8px 10px":"13px 18px",marginBottom:compact?4:8,boxShadow:"var(--sh0)",cursor:"pointer"}} onClick={()=>setDetailId(r.id)}>
        <div style={{display:"flex",alignItems:"flex-start",gap:compact?6:12}}>
          <div style={{width:compact?28:36,height:compact?28:36,borderRadius:"50%",background:r.status==="scheduled"?"var(--blu-bg)":r.status==="completed"?"var(--grn-bg)":"var(--g100)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <span style={{fontFamily:"var(--fm)",fontSize:compact?9:11,fontWeight:700,color:r.status==="scheduled"?"var(--blu)":r.status==="completed"?"var(--grn)":"var(--g400)",letterSpacing:".02em"}}>{initials}</span>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:compact?2:5,flexWrap:"wrap"}}>
              <span style={{fontFamily:"var(--fm)",fontSize:compact?11:13,fontWeight:600}}>{r.time}</span>
              <span className={`pill ${r.status==="scheduled"?"p-blu":r.status==="completed"?"p-done":"p-low"}`} style={{fontSize:compact?8:10}}>{enumLabel(r.status,"rideStatus",lang)}</span>
              {r.isRecurring&&<span className="pill p-gold" style={{fontSize:compact?7:9}}>↻ {r.frequencyLabel}</span>}
            </div>
            <div style={{fontSize:compact?11:14,fontWeight:500,marginBottom:compact?2:4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              <span style={{color:"var(--g600)"}}>{r.pickupLoc}</span>
              <span style={{margin:"0 6px",color:"var(--g300)"}}>→</span>
              <span>{r.dropoffLoc}</span>
            </div>
            {!compact&&(
              <div style={{fontSize:12,color:"var(--g400)",display:"flex",gap:12,flexWrap:"wrap"}}>
                {rider&&<span>{isRTL?"لـ":"For"}: {isRTL&&rider.nameAr?rider.nameAr:rider.name}</span>}
                {driver&&<span>{isRTL?"السائق":"Driver"}: {isRTL&&driver.nameAr?driver.nameAr:driver.name}</span>}
                {vehicle&&<span>{vehicleLabel(vehicle)}</span>}
              </div>
            )}
            {compact&&<div style={{fontSize:10,color:"var(--g400)"}}>{rider&&<span>{isRTL?"لـ":"For"}: {isRTL&&rider.nameAr?rider.nameAr:rider.name}</span>}{rider&&driver&&" · "}{driver&&<span>{isRTL&&driver.nameAr?driver.nameAr:driver.name}</span>}</div>}
          </div>
          <div style={{width:8,height:8,borderRadius:"50%",background:trnStatusColor(r.status),flexShrink:0,marginTop:compact?10:14}}/>
        </div>
      </div>
    );
  };

  /* Export filtered rides as CSV */
  const exportCSV=()=>{
    const rows=filtered.length?filtered:(transportRequests||[]);
    if(!rows.length)return;
    const header=["Date","Time","Status","Pickup","Dropoff","Rider","Driver","Vehicle","Recurring","Frequency","Notes"];
    const csvRows=[header.join(",")];
    for(const r of rows){
      const rider=users.find(u=>u.id===r.userId);
      const driver=users.find(u=>u.id===r.driverId);
      const veh=r.vehicleId?allVehicles.find(v=>v.id===r.vehicleId):null;
      const esc=(s)=>`"${(s||"").replace(/"/g,'""')}"`;
      csvRows.push([r.date,r.time,r.status,esc(r.pickupLoc),esc(r.dropoffLoc),esc(rider?.name),esc(driver?.name),esc(veh?vehicleLabel(veh):""),r.isRecurring?"Yes":"No",r.frequencyLabel||"",esc(r.notes)].join(","));
    }
    const blob=new Blob([csvRows.join("\n")],{type:"text/csv;charset=utf-8;"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");a.href=url;a.download=`transport-rides-${TODAY}.csv`;a.click();
    URL.revokeObjectURL(url);
  };

  /* ── Mobile computed values ── */
  const scheduledC=(transportRequests||[]).filter(r=>r.status==="scheduled").length;
  const completedC=(transportRequests||[]).filter(r=>r.status==="completed").length;
  const recurringC=[...new Set((transportRequests||[]).filter(r=>r.groupId).map(r=>r.groupId))].length;
  const cancelledC=(transportRequests||[]).filter(r=>r.status==="cancelled").length;
  const totalC=(transportRequests||[]).length;

  /* Mobile ride filtering */
  const trnMobileFiltered=(transportRequests||[]).filter(r=>{
    if(driverLock&&r.driverId!==driverLock)return false;
    if(filterStatus!=="all"&&r.status!==filterStatus)return false;
    if(trnDateFilter==="today")return r.date===TODAY;
    if(trnDateFilter==="week"){return weekDays.includes(r.date);}
    if(trnDateFilter==="upcoming")return r.date>=TODAY;
    return true;
  }).sort((a,b)=>a.date.localeCompare(b.date)||a.time.localeCompare(b.time));

  /* Status color helper */
  const trnStatusColor=(status)=>status==="scheduled"?"var(--blu)":status==="completed"?"var(--grn)":"var(--g400)";

  return(
    <div className="page">
      {vt.Toast}
      {/* Desktop header */}
      <div className="ph trn-hero-desktop">
        <div><div className="ptitle">{state.platformModuleNames?.Transport||(isRTL?"النقل":"Transportation")}</div><div className="psub">{state.moduleSubtitles?.Transport||(isRTL?"جدول النقل · رحلات متكررة":"Transport Schedule · One-off & Recurring")}</div></div>
        <div style={{display:"flex",gap:8}}>
          {totalC>0&&<button className="btn btn-g" onClick={exportCSV}>{isRTL?"تصدير":"Export CSV"}</button>}
          {canBook&&<button className="btn btn-p" onClick={()=>setShowAdd(true)}>+ {isRTL?"حجز رحلة":"Book Ride"}</button>}
        </div>
      </div>

      {/* Mobile hero header */}
      <div className="trn-hero">
        <div className="trn-hero-greeting">{state.platformModuleNames?.Transport||(isRTL?"النقل":"Transportation")}</div>
        <div className="trn-hero-sub">{state.moduleSubtitles?.Transport||(isRTL?"جدول النقل · رحلات متكررة":"Transport Schedule · One-off & Recurring")}</div>
        <div className="trn-summary-glass">
          <div className="trn-summary-row">
            <div className="trn-summary-item"><div className="trn-summary-val" style={{color:"var(--blu)"}}>{scheduledC}</div><div className="trn-summary-lbl">{isRTL?"مجدول":"Scheduled"}</div></div>
            <div className="trn-summary-item"><div className="trn-summary-val" style={{color:"var(--grn)"}}>{completedC}</div><div className="trn-summary-lbl">{isRTL?"مكتمل":"Completed"}</div></div>
            <div className="trn-summary-item"><div className="trn-summary-val" style={{color:"var(--gold)"}}>{recurringC}</div><div className="trn-summary-lbl">{isRTL?"متكرر":"Recurring"}</div></div>
            <div className="trn-summary-item"><div className="trn-summary-val" style={{color:"var(--g400)"}}>{cancelledC}</div><div className="trn-summary-lbl">{isRTL?"ملغى":"Cancelled"}</div></div>
          </div>
          {totalC>0&&<div className="trn-split-bar">
            {scheduledC>0&&<div className="trn-split-bar-seg" style={{width:`${(scheduledC/totalC)*100}%`,background:"var(--blu)"}}/>}
            {completedC>0&&<div className="trn-split-bar-seg" style={{width:`${(completedC/totalC)*100}%`,background:"var(--grn)"}}/>}
            {cancelledC>0&&<div className="trn-split-bar-seg" style={{width:`${(cancelledC/totalC)*100}%`,background:"var(--g400)"}}/>}
          </div>}
        </div>
      </div>

      {/* Mobile: Status filter tabs */}
      <div className="trn-status-tabs">
        {["all","scheduled","completed","cancelled"].map(s=>(
          <button key={s} className={`trn-status-tab ${filterStatus===s?"active":""}`} onClick={()=>{setFilterStatus(s);setTrnMobilePage(0);}}>{s==="all"?(isRTL?"الكل":"All"):s==="scheduled"?(isRTL?"مجدول":"Scheduled"):s==="completed"?(isRTL?"مكتمل":"Done"):(isRTL?"ملغى":"Cancelled")}</button>
        ))}
      </div>

      {/* Mobile: Horizontal scrolling date filter pills */}
      <div className="trn-pill-scroll">
        {[{k:"all",l:isRTL?"الكل":"All"},{k:"upcoming",l:isRTL?"القادمة":"Upcoming"},{k:"today",l:isRTL?"اليوم":"Today"},{k:"week",l:isRTL?"هذا الأسبوع":"This Week"}].map(f=>(
          <button key={f.k} className={`trn-pill-btn ${trnDateFilter===f.k?"active":""}`} onClick={()=>{setTrnDateFilter(f.k);setTrnMobilePage(0);}}>{f.l}</button>
        ))}
      </div>

      {showAdd&&(
        <Modal><div className="stf-detail-overlay" onClick={()=>setShowAdd(false)}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div className="stf-detail-title">{isRTL?"حجز تنقل":"Book Transportation"}</div>
              <button className="stf-detail-close" onClick={()=>setShowAdd(false)}>✕</button>
            </div>
            <div className="stf-detail-body">
              <div className="fr">
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"لـ":"For"}</label><select className="fs" value={form.userId} onChange={e=>setForm(p=>({...p,userId:e.target.value}))}>{[...familyUsers].sort((a,b)=>a.id===currentUser?.id?-1:b.id===currentUser?.id?1:0).map(u=><option key={u.id} value={u.id}>{isRTL&&u.nameAr?u.nameAr:u.name}{u.id===currentUser?.id?` (${isRTL?"أنت":"You"})`:""}</option>)}</select></div>
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"السائق":"Driver"}</label><select className="fs" value={form.driverId} onChange={e=>{const did=e.target.value;const autoV=vehicleForDriver(did);setForm(p=>({...p,driverId:did,vehicleId:autoV?.id||p.vehicleId}));}}><option value="">{isRTL?"تعيين تلقائي":"Auto-assign"}</option>{drivers.map(d=><option key={d.id} value={d.id}>{isRTL&&d.nameAr?d.nameAr:d.name}</option>)}</select></div>
              </div>
              {allVehicles.length>0&&(
                <div className="fg"><label className="fl">{isRTL?"المركبة":"Vehicle"}</label><select className="fs" value={form.vehicleId} onChange={e=>setForm(p=>({...p,vehicleId:e.target.value}))}><option value="">{isRTL?"تلقائي":"Auto"}</option>{allVehicles.map(v=><option key={v.id} value={v.id}>{vehicleLabel(v)}</option>)}</select></div>
              )}
              <div className="fr">
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"نقطة الانطلاق":"Pickup"}</label>
                  <PlacesAutocomplete apiKey={mapsApiKey} provider={mapsProvider} country={mapsCountry} proximity={mapsProximity} isRTL={isRTL} value={form.pickupLoc} placeholder={isRTL?"مثال: المنزل":"e.g. Riyadh Home"}
                    onChange={v=>setForm(p=>({...p,pickupLoc:v.address,pickupPlaceId:v.placeId,pickupLat:v.lat,pickupLng:v.lng,pickupMapsUrl:v.mapsUrl}))}/>
                </div>
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"الوجهة":"Dropoff"}</label>
                  <PlacesAutocomplete apiKey={mapsApiKey} provider={mapsProvider} country={mapsCountry} proximity={mapsProximity} isRTL={isRTL} value={form.dropoffLoc} placeholder={isRTL?"مثال: المكتب":"e.g. HQ Office"}
                    onChange={v=>setForm(p=>({...p,dropoffLoc:v.address,dropoffPlaceId:v.placeId,dropoffLat:v.lat,dropoffLng:v.lng,dropoffMapsUrl:v.mapsUrl}))}/>
                </div>
              </div>
              {locations.length>0&&(
                <div className="fg"><label className="fl">{isRTL?"الموقع":"Location"}</label><select className="fs" value={form.locId} onChange={e=>setForm(p=>({...p,locId:e.target.value}))}><option value="">{isRTL?"— اختر —":"— select —"}</option>{locations.map(l=><option key={l.id} value={l.id}>{isRTL&&l.nameAr?l.nameAr:l.name}</option>)}</select></div>
              )}
              <div className="fr">
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"التاريخ":"Date"}</label><input className="fi" type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))}/></div>
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"الوقت":"Time"}</label><input className="fi" type="time" value={form.time} onChange={e=>setForm(p=>({...p,time:e.target.value}))}/></div>
              </div>

              {/* Recurring toggle */}
              <div style={{padding:"12px 0 8px",borderTop:"1px solid var(--g100)",marginTop:4}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:form.recurring?12:0}}>
                  <div className={`toggle ${form.recurring?"on":""}`} onClick={()=>setForm(p=>({...p,recurring:!p.recurring}))}/>
                  <div style={{fontSize:13,fontWeight:500}}>{isRTL?"تكرار هذه الرحلة":"Repeat this ride"}</div>
                  {form.recurring&&<span className="pill p-blu" style={{fontSize:10}}>{isRTL?"متكرر":"Recurring"}</span>}
                </div>
                {form.recurring&&(
                  <div className="fr">
                    <div className="fg" style={{flex:1}}>
                      <label className="fl">{isRTL?"التكرار":"Frequency"}</label>
                      <select className="fs" value={form.frequency} onChange={e=>setForm(p=>({...p,frequency:e.target.value}))}>
                        <option value="daily">{isRTL?"يومياً":"Every day"}</option>
                        <option value="weekdays">{isRTL?"أيام العمل (أحد–خميس)":"Weekdays (Sun–Thu)"}</option>
                        <option value="weekly">{isRTL?"أسبوعياً":"Weekly"}</option>
                        <option value="biweekly">{isRTL?"كل أسبوعين":"Every 2 weeks"}</option>
                        <option value="monthly">{isRTL?"شهرياً":"Monthly"}</option>
                      </select>
                    </div>
                    <div className="fg" style={{flex:1}}>
                      <label className="fl">{isRTL?"التكرار حتى":"Repeat until"} <span style={{color:"var(--g400)",fontWeight:400}}>({isRTL?"اختياري":"optional"})</span></label>
                      <input className="fi" type="date" value={form.repeatUntil} onChange={e=>setForm(p=>({...p,repeatUntil:e.target.value}))} min={form.date}/>
                    </div>
                  </div>
                )}
                {form.recurring&&(
                  <div style={{fontSize:11,color:"var(--g500)",marginTop:4}}>
                    {form.repeatUntil
                      ?`${isRTL?"سيتم إنشاء":"Will create"} ${generateDates(form.date,form.frequency,form.repeatUntil).length} ${isRTL?"رحلة حتى":"rides until"} ${form.repeatUntil}`
                      :(isRTL?"سيتم إنشاء حتى 52 رحلة (90 يوم)":"Will create up to 52 rides (90 days)")}
                  </div>
                )}
              </div>

              {/* Conflict warning */}
              {conflicts.length>0&&(
                <div style={{background:"var(--red-bg,#fef2f2)",border:"1px solid var(--red-bd,#fecaca)",borderRadius:"var(--r)",padding:"10px 14px",marginTop:8}}>
                  <div style={{fontSize:12,fontWeight:600,color:"var(--red,#dc2626)",marginBottom:4}}>⚠ {isRTL?"تعارض في جدول السائق":"Driver scheduling conflict"}</div>
                  <div style={{fontSize:11,color:"var(--red,#dc2626)"}}>
                    {isRTL
                      ?`السائق لديه ${conflicts.length} رحلة أخرى خلال ${conflictBuffer} دقيقة من هذا الوقت:`
                      :`Driver has ${conflicts.length} existing ride${conflicts.length>1?"s":""} within ${conflictBuffer} min of this time:`}
                  </div>
                  <div style={{marginTop:6,display:"flex",flexDirection:"column",gap:4}}>
                    {conflicts.slice(0,5).map((c,i)=>(
                      <div key={i} style={{fontSize:11,color:"var(--red,#dc2626)",padding:"2px 0"}}>
                        {c.date} {c.time} · {c.pickupLoc} → {c.dropoffLoc}
                      </div>
                    ))}
                    {conflicts.length>5&&<div style={{fontSize:10,color:"var(--red,#dc2626)"}}>+{conflicts.length-5} more</div>}
                  </div>
                </div>
              )}

              <div className="fg"><label className="fl">{isRTL?"ملاحظات":"Notes"}</label><textarea className="fta" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} style={{minHeight:40}}/></div>
            </div>
            {bookErr&&<div style={{padding:"0 20px 8px",fontSize:12,color:"var(--red)",fontWeight:500}}>{bookErr}</div>}
            <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
              <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{setShowAdd(false);setBookErr("");}}>{isRTL?"إلغاء":"Cancel"}</button>
              <button className="btn btn-p" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={book}>
                {form.recurring?`${isRTL?"حجز":"Book"} ${form.repeatUntil?generateDates(form.date,form.frequency,form.repeatUntil).length:"+"} ${isRTL?"رحلة":"Rides"}`:(isRTL?"تأكيد الحجز":"Confirm Booking")}
              </button>
            </div>
          </div>
        </div></Modal>
      )}

      {/* Edit modal */}
      {editId&&canEdit&&(()=>{const r=(transportRequests||[]).find(x=>x.id===editId);if(!r)return null;return(
        <Modal><div className="stf-detail-overlay" onClick={()=>{setEditId(null);setEditApplyAll(false);}}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div className="stf-detail-title">{isRTL?"تعديل الرحلة":"Edit Ride"}</div>
              <button className="stf-detail-close" onClick={()=>{setEditId(null);setEditApplyAll(false);}}>✕</button>
            </div>
            <div className="stf-detail-body">
              <div className="fr">
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"نقطة الانطلاق":"Pickup"}</label>
                  <PlacesAutocomplete apiKey={mapsApiKey} provider={mapsProvider} country={mapsCountry} proximity={mapsProximity} isRTL={isRTL} value={editForm.pickupLoc||""} placeholder=""
                    onChange={v=>setEditForm(p=>({...p,pickupLoc:v.address,pickupPlaceId:v.placeId,pickupLat:v.lat,pickupLng:v.lng,pickupMapsUrl:v.mapsUrl}))}/>
                </div>
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"الوجهة":"Dropoff"}</label>
                  <PlacesAutocomplete apiKey={mapsApiKey} provider={mapsProvider} country={mapsCountry} proximity={mapsProximity} isRTL={isRTL} value={editForm.dropoffLoc||""} placeholder=""
                    onChange={v=>setEditForm(p=>({...p,dropoffLoc:v.address,dropoffPlaceId:v.placeId,dropoffLat:v.lat,dropoffLng:v.lng,dropoffMapsUrl:v.mapsUrl}))}/>
                </div>
              </div>
              <div className="fr">
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"التاريخ":"Date"}</label><input className="fi" type="date" value={editForm.date||""} onChange={e=>setEditForm(p=>({...p,date:e.target.value}))}/></div>
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"الوقت":"Time"}</label><input className="fi" type="time" value={editForm.time||""} onChange={e=>setEditForm(p=>({...p,time:e.target.value}))}/></div>
              </div>
              <div className="fr">
                <div className="fg" style={{flex:1}}><label className="fl">{isRTL?"السائق":"Driver"}</label><select className="fs" value={editForm.driverId||""} onChange={e=>{const did=e.target.value;const autoV=vehicleForDriver(did);setEditForm(p=>({...p,driverId:did,vehicleId:autoV?.id||p.vehicleId||""}));}}><option value="">{isRTL?"تعيين تلقائي":"Auto-assign"}</option>{drivers.map(d=><option key={d.id} value={d.id}>{isRTL&&d.nameAr?d.nameAr:d.name}</option>)}</select></div>
                {allVehicles.length>0&&<div className="fg" style={{flex:1}}><label className="fl">{isRTL?"المركبة":"Vehicle"}</label><select className="fs" value={editForm.vehicleId||""} onChange={e=>setEditForm(p=>({...p,vehicleId:e.target.value}))}><option value="">—</option>{allVehicles.map(v=><option key={v.id} value={v.id}>{vehicleLabel(v)}</option>)}</select></div>}
              </div>
              <div className="fg"><label className="fl">{isRTL?"ملاحظات":"Notes"}</label><textarea className="fta" value={editForm.notes||""} onChange={e=>setEditForm(p=>({...p,notes:e.target.value}))} style={{minHeight:40}}/></div>
              {r.groupId&&(
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0 4px",borderTop:"1px solid var(--g100)",marginTop:6}}>
                  <div className={`toggle ${editApplyAll?"on":""}`} onClick={()=>setEditApplyAll(a=>!a)}/>
                  <div style={{fontSize:12,fontWeight:500}}>{isRTL?"تطبيق على جميع الرحلات القادمة":"Apply to all future rides in this series"}</div>
                </div>
              )}
            </div>
            {r.groupId&&(
              <div style={{padding:"0 20px 8px",display:"flex",justifyContent:"center"}}>
                <button className="btn btn-d" style={{borderRadius:14,padding:"10px 20px",fontSize:12,fontWeight:600,textAlign:"center"}} onClick={()=>{cancelFutureSeries(r.groupId);setEditId(null);setEditApplyAll(false);}}>{isRTL?"إلغاء السلسلة":"Cancel Series"}</button>
              </div>
            )}
            <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
              <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{setEditId(null);setEditApplyAll(false);}}>{isRTL?"إلغاء":"Cancel"}</button>
              <button className="btn btn-p" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={saveEdit}>{editApplyAll?(isRTL?"حفظ الكل":"Save All"):(isRTL?"حفظ":"Save")}</button>
            </div>
          </div>
        </div></Modal>);})()}

      {/* ═══ RIDE DETAIL BOTTOM SHEET ═══ */}
      {detailId&&(()=>{const r=(transportRequests||[]).find(x=>x.id===detailId);if(!r)return null;
        const driver=users.find(u=>u.id===r.driverId);
        const rider=users.find(u=>u.id===r.userId);
        const vehicle=r.vehicleId?allVehicles.find(v=>v.id===r.vehicleId):null;
        const initials=vehicle?vehicleInitials(vehicle):"--";
        return(
        <Modal><div className="stf-detail-overlay" onClick={()=>setDetailId(null)}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div>
                <div className="stf-detail-title">{r.pickupLoc} → {r.dropoffLoc}</div>
                <div className="stf-detail-sub">{r.date} · {r.time}</div>
              </div>
              <button className="stf-detail-close" onClick={()=>setDetailId(null)}>✕</button>
            </div>
            <div className="stf-detail-body">
              <div className="stf-detail-row"><div className="stf-detail-row-label">{isRTL?"الحالة":"Status"}</div><div className="stf-detail-row-value"><span className={`pill ${r.status==="scheduled"?"p-blu":r.status==="completed"?"p-done":"p-low"}`}>{enumLabel(r.status,"rideStatus",lang)}</span></div></div>
              {rider&&<div className="stf-detail-row"><div className="stf-detail-row-label">{isRTL?"لـ":"For"}</div><div className="stf-detail-row-value">{isRTL&&rider.nameAr?rider.nameAr:rider.name}</div></div>}
              {driver&&<div className="stf-detail-row"><div className="stf-detail-row-label">{isRTL?"السائق":"Driver"}</div><div className="stf-detail-row-value">{isRTL&&driver.nameAr?driver.nameAr:driver.name}</div></div>}
              {vehicle&&<div className="stf-detail-row"><div className="stf-detail-row-label">{isRTL?"المركبة":"Vehicle"}</div><div className="stf-detail-row-value">{vehicleLabel(vehicle)}</div></div>}
              <div className="stf-detail-row"><div className="stf-detail-row-label">{isRTL?"نقطة الانطلاق":"Pickup"}</div><div className="stf-detail-row-value">{r.pickupMapsUrl?<a href={r.pickupMapsUrl} target="_blank" rel="noopener noreferrer" onClick={e=>{e.preventDefault();openExternalUrl(r.pickupMapsUrl);}} style={{color:"var(--blu)",textDecoration:"none"}}>{r.pickupLoc}</a>:r.pickupLoc}</div></div>
              <div className="stf-detail-row"><div className="stf-detail-row-label">{isRTL?"الوجهة":"Dropoff"}</div><div className="stf-detail-row-value">{r.dropoffMapsUrl?<a href={r.dropoffMapsUrl} target="_blank" rel="noopener noreferrer" onClick={e=>{e.preventDefault();openExternalUrl(r.dropoffMapsUrl);}} style={{color:"var(--blu)",textDecoration:"none"}}>{r.dropoffLoc}</a>:r.dropoffLoc}</div></div>
              {r.directionsUrl&&<div className="stf-detail-row"><div className="stf-detail-row-label">{isRTL?"الاتجاهات":"Directions"}</div><div className="stf-detail-row-value"><a href={r.directionsUrl} target="_blank" rel="noopener noreferrer" onClick={e=>{e.preventDefault();openExternalUrl(r.directionsUrl);}} style={{color:"var(--blu)",textDecoration:"none"}}>{isRTL?"فتح الخريطة":"Open Map"}</a></div></div>}
              {r.isRecurring&&<div className="stf-detail-row"><div className="stf-detail-row-label">{isRTL?"التكرار":"Recurring"}</div><div className="stf-detail-row-value"><span className="pill p-gold">↻ {r.frequencyLabel}</span></div></div>}
              {r.notes&&<div className="stf-detail-row"><div className="stf-detail-row-label">{isRTL?"ملاحظات":"Notes"}</div><div className="stf-detail-row-value">{r.notes}</div></div>}
              {!r.taskId&&failedTaskIds.includes(r.id)&&tCfg.autoCreateTask!==false&&(
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0"}}>
                  <span style={{fontSize:12,color:"var(--red,#dc2626)"}}>⚠ {isRTL?"فشل إنشاء المهمة":"Task creation failed"}</span>
                  <button className="btn btn-g btn-xs" style={{fontSize:10,color:"var(--blu)"}} onClick={()=>retryTaskCreation(r.id)}>↻ {isRTL?"إعادة":"Retry"}</button>
                </div>
              )}
            </div>
            {/* Scheduled-ride action row. Owner (canEdit) gets full controls
                — Cancel, Edit (re-book), Done. A staff driver (driverLock
                === r.driverId) gets status transitions only — Cancel and
                Done — since they can't re-book the trip themselves. */}
            {r.status==="scheduled"&&(canEdit||driverLock===r.driverId)&&(
              <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                <button className="btn btn-d" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{updateRideStatus(r.id,"cancelled");setDetailId(null);}}>{isRTL?"إلغاء":"Cancel"}</button>
                {canEdit&&<button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{setDetailId(null);setEditId(r.id);setEditForm({pickupLoc:r.pickupLoc,dropoffLoc:r.dropoffLoc,date:r.date,time:r.time,driverId:r.driverId,vehicleId:r.vehicleId||"",notes:r.notes});setEditApplyAll(false);}}>{isRTL?"تعديل":"Edit"}</button>}
                <button className="btn btn-s" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{updateRideStatus(r.id,"completed");setDetailId(null);}}>{isRTL?"تم":"Done"}</button>
              </div>
            )}
            {r.status!=="scheduled"&&(
              <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setDetailId(null)}>{isRTL?"إغلاق":"Close"}</button>
              </div>
            )}
          </div>
        </div></Modal>);})()}

      {/* ═══ DESKTOP SECTION — stats, view toggle, week/list views ═══ */}
      <div className="trn-desktop-section">
        <div className="g4" style={{marginBottom:20}}>
          {[
            {l:isRTL?"مجدول":"Scheduled",v:scheduledC},
            {l:isRTL?"مكتمل":"Completed",v:completedC},
            {l:isRTL?"متكرر":"Recurring",v:recurringC},
            {l:isRTL?"ملغى":"Cancelled",v:cancelledC},
          ].map(x=><div key={x.l} className="stat"><div className="stat-l">{x.l}</div><div className="stat-v">{x.v}</div></div>)}
        </div>

        {/* View toggle + Filter */}
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          <div style={{display:"flex",gap:2,background:"var(--g100)",borderRadius:8,padding:2}}>
            {[{k:"week",l:isRTL?"أسبوع":"Week"},{k:"list",l:isRTL?"قائمة":"List"}].map(v=>(
              <button key={v.k} onClick={()=>setViewMode(v.k)} style={{padding:"5px 14px",fontSize:12,fontWeight:600,borderRadius:6,border:"none",cursor:"pointer",background:viewMode===v.k?"var(--white)":"transparent",color:viewMode===v.k?"var(--ink)":"var(--g400)",boxShadow:viewMode===v.k?"var(--sh0)":"none",transition:"all .15s"}}>{v.l}</button>
            ))}
          </div>
          <div style={{flex:1}}/>
          {["all","scheduled","completed","cancelled"].map(s=>(
            <button key={s} className={`btn btn-sm ${filterStatus===s?"btn-p":"btn-s"}`} onClick={()=>setFilterStatus(s)} style={{textTransform:"capitalize"}}>{s==="all"?(isRTL?"الكل":"All"):enumLabel(s,"rideStatus",lang)}</button>
          ))}
        </div>

        {/* ── WEEK VIEW ── */}
        {viewMode==="week"&&(
          <div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <button className="btn btn-g btn-sm" onClick={()=>setWeekOffset(w=>w-1)}>← {isRTL?"السابق":"Prev"}</button>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:13,fontWeight:600,color:"var(--ink)"}}>{weekLabel}</span>
                {weekOffset!==0&&<button className="btn btn-g btn-xs" onClick={()=>setWeekOffset(0)}>{isRTL?"اليوم":"Today"}</button>}
              </div>
              <button className="btn btn-g btn-sm" onClick={()=>setWeekOffset(w=>w+1)}>{isRTL?"التالي":"Next"} →</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:6,minHeight:200}}>
              {weekDays.map((day,i)=>{
                const dayRides=(byDate[day]||[]).sort((a,b)=>(a.time||"").localeCompare(b.time||""));
                const isToday=day===TODAY;
                const isWeekend=i>=5; /* Fri=5, Sat=6 */
                return(
                  <div key={day} style={{background:isToday?"var(--accent-bg,#faf6eb)":isWeekend?"var(--g50)":"var(--white)",border:"1px solid "+(isToday?"var(--accent,#C9A84C)":"var(--g200)"),borderRadius:"var(--r2)",padding:"8px 6px",minHeight:120,display:"flex",flexDirection:"column"}}>
                    <div style={{textAlign:"center",marginBottom:6}}>
                      <div style={{fontSize:10,fontWeight:600,color:isToday?"var(--accent,#C9A84C)":"var(--g400)",textTransform:"uppercase",letterSpacing:".05em"}}>{isRTL?dayLabelsAr[i]:dayLabelsEn[i]}</div>
                      <div style={{fontSize:16,fontWeight:700,color:isToday?"var(--accent,#C9A84C)":"var(--ink)"}}>{day.slice(8)}</div>
                    </div>
                    <div style={{flex:1,display:"flex",flexDirection:"column",gap:3}}>
                      {dayRides.map(r=>renderRideCard(r,true))}
                      {!dayRides.length&&<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"var(--g300)"}}>{isWeekend?(isRTL?"عطلة":"Weekend"):"—"}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── LIST VIEW ── */}
        {viewMode==="list"&&(
          <div>
            {sortedDates.length===0&&<div className="empty">{isRTL?"لا توجد حجوزات نقل بعد.":"No transport bookings yet."}</div>}
            {sortedDates.map(date=>(
              <div key={date} style={{marginBottom:20}}>
                <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:8}}>
                  {date===TODAY?(isRTL?"اليوم":"Today"):date}
                </div>
                {byDate[date].map(r=>renderRideCard(r,false))}
              </div>
            ))}
          </div>
        )}
      </div>{/* end trn-desktop-section */}

      {/* ═══ MOBILE: Rides list (card-based, filterable) ═══ */}
      <div className="trn-mobile-list">
        <div className="trn-section-title">{isRTL?"الرحلات":"Rides"} <span style={{fontFamily:"var(--fm)",fontWeight:600,fontSize:11,color:"var(--blu)",marginInlineStart:6}}>{trnMobileFiltered.length}</span></div>
        {(()=>{
          if(!trnMobileFiltered.length) return <div style={{textAlign:"center",padding:"40px 20px",color:"var(--g400)",fontSize:14}}>
            {isRTL?"لا توجد رحلات":"No rides found"}
          </div>;

          const PER_PAGE=10;
          let visible;
          if(trnMobilePage===0) visible=trnMobileFiltered.slice(0,5);
          else if(trnMobilePage===1) visible=trnMobileFiltered.slice(0,PER_PAGE);
          else visible=trnMobileFiltered.slice((trnMobilePage-1)*PER_PAGE, trnMobilePage*PER_PAGE);
          const totalPages=trnMobileFiltered.length<=10?0:Math.ceil((trnMobileFiltered.length-PER_PAGE)/PER_PAGE)+1;
          const maxPage=Math.max(totalPages,1);

          return <div className="trn-merged-card">
            {visible.map(r=>{
              const driver=users.find(u=>u.id===r.driverId);
              const rider=users.find(u=>u.id===r.userId);
              const veh=r.vehicleId?allVehicles.find(v=>v.id===r.vehicleId):null;
              const isToday=r.date===TODAY;
              const initials=veh?vehicleInitials(veh):(r.isRecurring?"↻":"--");
              return(
              <div key={r.id} className="trn-row" style={{gap:12,cursor:"pointer"}} onClick={()=>setDetailId(r.id)}>
                <div style={{width:40,height:40,borderRadius:"50%",background:r.status==="scheduled"?"var(--blu-bg)":r.status==="completed"?"var(--grn-bg)":"var(--g100)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{fontFamily:"var(--fm)",fontSize:12,fontWeight:700,color:r.status==="scheduled"?"var(--blu)":r.status==="completed"?"var(--grn)":"var(--g400)",letterSpacing:".02em"}}>{initials}</span>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
                    <span style={{fontFamily:"var(--fm)",color:"var(--g500)",fontSize:11,fontWeight:600}}>{r.time}</span>
                    <span className={`pill ${r.status==="scheduled"?"p-blu":r.status==="completed"?"p-done":"p-low"}`} style={{fontSize:9}}>{enumLabel(r.status,"rideStatus",lang)}</span>
                    {r.isRecurring&&<span className="pill p-gold" style={{fontSize:8}}>↻ {r.frequencyLabel}</span>}
                  </div>
                  <div style={{fontSize:13,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    <span style={{color:"var(--g600)"}}>{r.pickupLoc}</span>
                    <span style={{margin:"0 6px",color:"var(--g300)"}}>→</span>
                    <span>{r.dropoffLoc}</span>
                  </div>
                  <div style={{fontSize:11,color:"var(--g400)",marginTop:2}}>
                    {isToday?(isRTL?"اليوم":"Today"):r.date}
                    {rider?` · ${isRTL&&rider.nameAr?rider.nameAr:rider.name}`:""}
                    {driver?` · ${isRTL&&driver.nameAr?driver.nameAr:driver.name}`:""}
                  </div>
                </div>
                <div style={{width:8,height:8,borderRadius:"50%",background:trnStatusColor(r.status),flexShrink:0,alignSelf:"center"}}/>
              </div>);
            })}
            {trnMobilePage===0&&trnMobileFiltered.length>5&&(
              <div className="trn-see-more" onClick={()=>setTrnMobilePage(1)}>
                {isRTL?`عرض المزيد (${trnMobileFiltered.length})`:`See more (${trnMobileFiltered.length})`}
              </div>
            )}
            {trnMobilePage>=1&&trnMobileFiltered.length>PER_PAGE&&(
              <div className="trn-page-nav">
                <button className="trn-page-btn" onClick={()=>setTrnMobilePage(p=>Math.max(1,p-1))} disabled={trnMobilePage<=1} style={{opacity:trnMobilePage<=1?.3:1}}>‹</button>
                <span style={{fontSize:13,fontWeight:600,color:"var(--ink)",minWidth:60,textAlign:"center",alignSelf:"center"}}>{trnMobilePage<=1?1:trnMobilePage-1} / {maxPage}</span>
                <button className="trn-page-btn" onClick={()=>setTrnMobilePage(p=>Math.min(maxPage+1,p+1))} disabled={trnMobilePage>maxPage} style={{opacity:trnMobilePage>maxPage?.3:1}}>›</button>
              </div>
            )}
          </div>;
        })()}
      </div>

      {/* ── MOBILE: FAB for booking rides ── */}
      {canBook&&<div className="trn-fab-group">
        <button className="trn-fab" onClick={()=>setShowAdd(true)} title={isRTL?"حجز رحلة":"Book Ride"}>+</button>
      </div>}

      {/* ── MOBILE ANALYTICS — Transport stats in iOS glass cards.
           Hidden under driverLock since staff should only see their own
           rides, not cross-driver workload breakdowns. ── */}
      {!driverLock&&(transportRequests||[]).length>0&&(()=>{
        const accent=state.branding?.anchorColor||"#C9A84C";
        const allReqs=transportRequests||[];
        const todayRides=allReqs.filter(r=>r.date===TODAY);
        const todaySched=todayRides.filter(r=>r.status==="scheduled").length;
        const todayDone=todayRides.filter(r=>r.status==="completed").length;

        /* Rides by driver */
        const byDriver={};
        allReqs.filter(r=>r.status!=="cancelled").forEach(r=>{
          const d=users.find(u=>u.id===r.driverId);
          const name=d?(isRTL&&d.nameAr?d.nameAr:d.name):(isRTL?"غير معين":"Unassigned");
          byDriver[name]=(byDriver[name]||0)+1;
        });
        const driverEntries=Object.entries(byDriver).sort((a,b)=>b[1]-a[1]);
        const maxDriverRides=Math.max(...driverEntries.map(e=>e[1]),1);

        /* Rides by day of week */
        const byDayOfWeek=[0,0,0,0,0,0,0];
        allReqs.filter(r=>r.status!=="cancelled").forEach(r=>{
          const d=new Date(r.date);
          byDayOfWeek[d.getDay()]++;
        });
        const maxDayRides=Math.max(...byDayOfWeek,1);

        /* Recurring vs one-off */
        const recurringRides=allReqs.filter(r=>r.isRecurring).length;
        const oneOffRides=allReqs.filter(r=>!r.isRecurring).length;

        /* Most common routes */
        const routeMap={};
        allReqs.filter(r=>r.status!=="cancelled").forEach(r=>{
          const key=`${r.pickupLoc} → ${r.dropoffLoc}`;
          routeMap[key]=(routeMap[key]||0)+1;
        });
        const topRoutes=Object.entries(routeMap).sort((a,b)=>b[1]-a[1]).slice(0,5);

        const COLORS=brandChartPalette(accent);

        return(
        <div className="trn-mobile-analytics">
          {/* 1. Today overview */}
          <div className="trn-mini-chart" style={{padding:0,overflow:"hidden"}}>
            <div style={{background:`linear-gradient(135deg, color-mix(in srgb, ${accent} 8%, transparent), color-mix(in srgb, var(--blu) 6%, transparent))`,padding:"20px 18px 18px"}}>
              <div className="trn-mini-chart-title" style={{marginBottom:16}}>{isRTL?"اليوم":"Today"}</div>
              <div style={{display:"flex",gap:24,alignItems:"center"}}>
                <MiniRing value={todayDone} max={Math.max(todayRides.length,1)} color={accent} size={90} label={todayRides.length} sub={isRTL?"رحلات":"rides"} trackColor={`color-mix(in srgb, ${accent} 12%, transparent)`}/>
                <div style={{flex:1,display:"flex",flexDirection:"column",gap:14}}>
                  <div style={{display:"flex",alignItems:"baseline",gap:8}}>
                    <div style={{fontFamily:"var(--fm)",fontSize:28,fontWeight:700,color:"var(--blu)",lineHeight:1}}>{todaySched}</div>
                    <div style={{fontFamily:"var(--fi)",fontSize:11,fontWeight:500,color:"var(--g400)",lineHeight:1.2}}>{isRTL?"مجدول":"scheduled"}</div>
                  </div>
                  <div style={{display:"flex",alignItems:"baseline",gap:8}}>
                    <div style={{fontFamily:"var(--fm)",fontSize:28,fontWeight:700,color:"var(--grn)",lineHeight:1}}>{todayDone}</div>
                    <div style={{fontFamily:"var(--fi)",fontSize:11,fontWeight:500,color:"var(--g400)",lineHeight:1.2}}>{isRTL?"مكتمل":"completed"}</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Ride type breakdown — bottom strip */}
            <div style={{display:"flex",borderTop:"1px solid rgba(0,0,0,.04)"}}>
              {[{l:isRTL?"متكررة":"Recurring",v:recurringRides,c:"var(--gold)"},{l:isRTL?"مرة واحدة":"One-off",v:oneOffRides,c:"var(--blu)"},{l:isRTL?"ملغاة":"Cancelled",v:cancelledC,c:"var(--g400)"}].map((p,i)=>(
                <div key={i} style={{flex:1,padding:"12px 10px",textAlign:"center",borderRight:i<2?"1px solid rgba(0,0,0,.04)":"none"}}>
                  <div style={{fontFamily:"var(--fi)",fontSize:9,fontWeight:700,color:p.c,textTransform:"uppercase",letterSpacing:".03em",marginBottom:3}}>{p.l}</div>
                  <div style={{fontFamily:"var(--fm)",fontSize:16,fontWeight:700,color:p.c,lineHeight:1}}>{p.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Driver workload */}
          {driverEntries.length>0&&<div className="trn-mini-chart">
            <div className="trn-mini-chart-title">{isRTL?"توزيع السائقين":"Driver Workload"}</div>
            {driverEntries.map(([name,count],i)=>(
              <div key={name} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:i<driverEntries.length-1?"1px solid rgba(0,0,0,.04)":"none"}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:COLORS[i%COLORS.length],flexShrink:0}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{name} <span style={{fontFamily:"var(--fm)",fontSize:10,color:"var(--g400)",marginInlineStart:4}}>{totalC?(count/totalC*100).toFixed(0):0}%</span></div>
                  <div style={{height:4,borderRadius:2,background:"var(--g100)",marginTop:4,overflow:"hidden"}}>
                    <div style={{height:"100%",borderRadius:2,width:`${(count/maxDriverRides)*100}%`,background:COLORS[i%COLORS.length],transition:"width .4s ease"}}/>
                  </div>
                </div>
                <div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap"}}>{count}</div>
              </div>
            ))}
          </div>}

          {/* 3. Busiest days — horizontal bar */}
          <div className="trn-mini-chart">
            <div className="trn-mini-chart-title">{isRTL?"أيام الأسبوع":"Busiest Days"}</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {byDayOfWeek.map((count,i)=>{
                const label=isRTL?dayLabelsAr[i]:dayLabelsEn[i];
                return(
                <div key={i} style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{fontFamily:"var(--fi)",fontSize:11,fontWeight:600,color:"var(--g500)",width:32,flexShrink:0}}>{label}</div>
                  <div style={{flex:1,height:8,borderRadius:4,background:"var(--g100)",overflow:"hidden"}}>
                    <div style={{height:"100%",borderRadius:4,width:`${(count/maxDayRides)*100}%`,background:count>0?accent:"transparent",transition:"width .4s ease"}}/>
                  </div>
                  <div style={{fontFamily:"var(--fm)",fontSize:11,fontWeight:600,color:"var(--ink)",width:20,textAlign:"right"}}>{count}</div>
                </div>);
              })}
            </div>
          </div>

          {/* 4. Top routes */}
          {topRoutes.length>0&&<div className="trn-mini-chart">
            <div className="trn-mini-chart-title">{isRTL?"أكثر المسارات":"Top Routes"}</div>
            {topRoutes.map(([route,count],i)=>(
              <div key={route} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:i<topRoutes.length-1?"1px solid rgba(0,0,0,.04)":"none"}}>
                <div style={{fontFamily:"var(--fm)",fontSize:14,fontWeight:700,color:COLORS[i%COLORS.length],width:22,textAlign:"center"}}>{i+1}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{route}</div>
                </div>
                <div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:"var(--ink)"}}>{count}×</div>
              </div>
            ))}
          </div>}
        </div>);
      })()}
    </div>
  );
}


export { loadGoogleMaps, PlacesAutocomplete };
export default TransportModule;
